import React, {useContext, useEffect, useState} from 'react'
import {
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    InputAdornment,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import {AppContext} from '../../contexts/AppContextProvider'
import {CancelButton, PreviousButton} from '../StepperButtons'
import {AbutmentsInfoState, MaterialResponse} from '../../contexts/types'
import {useForm} from '../../hooks/useForm'
import axios from 'axios'
import http from '../../http'
import {OrgCustomizationContext} from "../../themes/OrgCustomizationProvider";
import {setAbutmentsInfo} from "../../contexts/AbutmentsInfoReducer";
import UseTheHackiestPageNavigation from "../../hooks/useTheHackiestPageNavigation";

const AbutmentsInformation: React.FC = () => {
    const {navigateForward} = UseTheHackiestPageNavigation()
    const {
        complaint,
        dispatch,
        customerNumber,
        countryCode,
        postNotification,
        clearNotification
    } = useContext(AppContext)
    const {t} = useContext(OrgCustomizationContext)

    // Article Number
    const [isValidatingArticleNum, setIsValidatingArticleNum] =
        useState<boolean>(false)
    const [isArticleNumValid, setIsArticleNumValid] = useState<boolean>(true)
    const articleNumInitialValue: string = complaint.abutmentsInfo.articleNumber as string

    // Batch Number
    const [isValidatingBatchNum, setIsValidatingBatchNum] =
        useState<boolean>(false)
    const [isBatchNumValid, setIsBatchNumValid] = useState<boolean>(true)
    const batchNumInitialValue: string = complaint.abutmentsInfo.batchOrLotNumber as string

    const [showBatchNumCheck, setShowBatchNumCheck] = useState<boolean>(false)
    const required = {value: true, message: t('this_field_is_required')}
    const {
        data,
        errors,
        handleChangeEvent,
        validateFields,
        enableOnChangeValidation,
    } = useForm<AbutmentsInfoState>({
        initialValues: {
            ...complaint.abutmentsInfo,
        },
        validations: {
            implantBrandUsed: {required},
        },
    })

    const navigateToNextScreen = () => {
        navigateForward()
    }
    
    // Validate article number
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source()
        const articleNum = data.articleNumber

        const validateArticleNum = async (articleNum: string) => {
            try {
                setIsValidatingArticleNum(true)

                const response = await http.get<MaterialResponse>(`/api/Product/Validate?material=${data.articleNumber}&customerNumber=${customerNumber}&countryCode=${countryCode}`, {cancelToken: cancelTokenSource.token})

                setIsArticleNumValid(response.data.materialList.some(m => m.materialReturnCode === '200'))
                setIsValidatingArticleNum(false)
            } catch (e) {
            }
        }

        if (articleNum) {
            if (articleNum !== articleNumInitialValue) {
                validateArticleNum(articleNum)
            } else {
                setIsArticleNumValid(true)
                setIsValidatingArticleNum(false)
            }
        } else {
            setIsArticleNumValid(true)
            setIsValidatingArticleNum(false)
            data.batchOrLotNumber = ''
            setShowBatchNumCheck(false)
        }

        return () => {
            cancelTokenSource.cancel()
        }
    }, [data.articleNumber, articleNumInitialValue])

    const handleBatchNumberBlur = () => {

        const cancelTokenSource = axios.CancelToken.source()
        const batchNum = data.batchOrLotNumber

        const validateBatchNum = async (batchNum: string) => {
            try {
                setIsValidatingBatchNum(true)

                const response = await http.get<MaterialResponse>(`/api/Product/Validate?material=${data.articleNumber}&batch=${batchNum}&customerNumber=${customerNumber}&countryCode=${countryCode}`, {cancelToken: cancelTokenSource.token})

                setIsBatchNumValid(response.data.materialList.some(m => m.batchReturnCode === '300'))
                setShowBatchNumCheck(response.data.materialList.some(m => m.batchReturnCode === '300'))

                setIsValidatingBatchNum(false)
            } catch (e) {
            }
        }

        if (isArticleNumValid && batchNum) {
            if (batchNum !== batchNumInitialValue) {
                validateBatchNum(batchNum)
            } else {
                setIsBatchNumValid(true)
                setShowBatchNumCheck(true)
                setIsValidatingBatchNum(false)
            }
        } else {
            setIsBatchNumValid(true)
            setShowBatchNumCheck(false)
            setIsValidatingBatchNum(false)
        }

        return () => {
            cancelTokenSource.cancel()
        }
    }

    const handleSubmit = () => {
        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // Validate the form fields
        let isFormValid = validateFields(data)
        if (isFormValid) {
            isFormValid = isArticleNumValid
            if (isFormValid) {
                isFormValid = isBatchNumValid
            }
        }

        // Stop processing if the form has invalid fields
        if (!isFormValid) {
            postNotification('error', t('error_mess'))
            return
        }

        // Form is valid, proceed with processing

        // Close the alert message in case it is open
        clearNotification()

        // Package up the form data and update app state
        const abutmentsInfo: AbutmentsInfoState = {
            ...data,
            batchOrLotNumber:
                isBatchNumValid && isArticleNumValid
                    ? data.batchOrLotNumber
                    : '',
        }
        
        dispatch(setAbutmentsInfo(abutmentsInfo))

        navigateToNextScreen()
    }

    return (
        <Grid container>
            <Stack spacing={2}>
                <Typography variant='h5' sx={{mb: 1}}>
                    {t('ai_abutments_information')}
                </Typography>

                <FormControl sx={{pt: 2}}>
                    <FormLabel>{t('ai_which_implant')}</FormLabel>
                    <RadioGroup
                        value={data.implantBrandUsed}
                        onChange={handleChangeEvent('implantBrandUsed')}
                    >
                        <FormControlLabel
                            value='ORIGINAL_NOBEL_BIOCARE'
                            control={<Radio/>}
                            label={t('ai_original_nobel_biocare') as string}
                        />
                        <FormControlLabel
                            value='UNKNOWN'
                            control={<Radio/>}
                            label={t('ai_unknown') as string}
                        />
                        <FormControlLabel
                            value='NON_NOBEL_BIOCARE'
                            control={<Radio/>}
                            label={t('ai_non_nobel_biocare') as string}
                        />
                    </RadioGroup>
                    <FormHelperText error={!!errors.implantBrandUsed}>
                        {errors.implantBrandUsed}
                    </FormHelperText>
                </FormControl>

                <Stack spacing={0}>
                    <FormControl>
                        <FormLabel sx={{mb: 1}}>
                            {t('ai_article_number')} (
                            {t('optional').toLowerCase()}):
                        </FormLabel>
                        <TextField
                            size='small'
                            sx={{maxWidth: '20rem'}}
                            error={!isArticleNumValid}
                            helperText={
                                !isArticleNumValid
                                    ? 'A valid article number is required'
                                    : !data.articleNumber
                                        ? 'Please enter a number if known'
                                        : null
                            }
                            value={data.articleNumber}
                            onChange={handleChangeEvent('articleNumber')}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        {isValidatingArticleNum && (
                                            <CircularProgress size='1rem'/>
                                        )}
                                        {!isValidatingArticleNum &&
                                        isArticleNumValid &&
                                        data.articleNumber ? (
                                            <CheckIcon
                                                sx={{
                                                    color: '#666)',
                                                }}
                                            />
                                        ) : null}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </FormControl>
                </Stack>

                {isArticleNumValid &&
                    !!data.articleNumber &&
                    !isValidatingArticleNum && (
                        <Stack spacing={0}>
                            <FormControl>
                                <FormLabel sx={{mb: 1}}>
                                    {t('ai_batch_lot_number')} (
                                    {t('optional').toLowerCase()}):
                                </FormLabel>
                                <TextField
                                    size='small'
                                    sx={{maxWidth: '20rem'}}
                                    error={!isBatchNumValid}
                                    helperText={
                                        !isBatchNumValid
                                            ? 'A valid batch or lot number is required'
                                            : !data.batchOrLotNumber
                                                ? 'Please enter a number if known'
                                                : null
                                    }
                                    value={data.batchOrLotNumber}
                                    onChange={handleChangeEvent(
                                        'batchOrLotNumber'
                                    )}
                                    inputProps={{maxLength: 10}}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                {isValidatingBatchNum && (
                                                    <CircularProgress size='1rem'/>
                                                )}
                                                {showBatchNumCheck &&
                                                    !isValidatingBatchNum &&
                                                    isBatchNumValid &&
                                                    data.batchOrLotNumber && (
                                                        <CheckIcon
                                                            sx={{
                                                                color: '#666)',
                                                            }}
                                                        />
                                                    )}
                                            </InputAdornment>
                                        ),
                                    }}
                                    onBlur={handleBatchNumberBlur}
                                />
                            </FormControl>
                        </Stack>
                    )}
            </Stack>

            <Stack
                direction='row-reverse'
                spacing={2}
                sx={{pt: 4, width: '100%'}}
            >
                <Button
                    variant='contained'
                    color='primary'
                    disabled={isValidatingArticleNum || isValidatingBatchNum}
                    onClick={() => handleSubmit()}
                >
                    {t('continue')}
                </Button>

                <PreviousButton before={() => dispatch(setAbutmentsInfo(data))} />

                <CancelButton/>
            </Stack>
        </Grid>
    )
}

export default AbutmentsInformation
